/**
 * NDT quotes (batch 5, fieldiq-api#1166) on the shared quote screens.
 *
 * Every helper here answers for an NDT quote only and returns null for any
 * other vertical, so the shared screens keep exactly what they rendered before
 * for casing, rentals, downhole and pressure testing.
 */
import { NDT_LINE_UNIT_LABEL } from './ndt-tickets';

/**
 * The currency an NDT quote's money is spelled in: the quote row's own code
 * (api quotes.currency, stamped from companies.currency at create). Null for any
 * other vertical, which keeps fmtCurrency's bare "$" there, unchanged.
 */
export function ndtQuoteCurrency(quote: { vertical?: string | null; currency?: string | null } | null | undefined): string | null {
  if (!quote || quote.vertical !== 'ndt_inspection') return null;
  return quote.currency === 'CAD' || quote.currency === 'USD' ? quote.currency : null;
}

/**
 * The quote line's unit (api lib/quote-pricing/ndt.ts ndtQuoteUnit) back to the
 * rate line's kind. The API writes every NDT line as line_item_type 'other', so
 * the kind is read from the unit it stamps.
 */
const NDT_QUOTE_UNIT_KIND: Record<string, string> = {
  per_weld: 'per_weld',
  per_shot: 'per_shot',
  per_crew_day: 'per_crew_day',
  per_hour: 'per_hour',
  per_km: 'per_distance',
  per_mile: 'per_distance',
  flat_fee: 'flat',
  each: 'consumable',
};

/**
 * The Type column for a line priced from the NDT rate card (source_kind
 * 'ndt_rate'): "Per weld", "Per shot / film", "Per distance", "Flat charge"…
 * Null for any other line, which keeps its own Type rendering. An NDT line with
 * a unit outside the seven is `—`, never "Other".
 */
export function ndtQuoteLineKind(line: { source_kind?: string | null; unit?: string | null }): string | null {
  if (line.source_kind !== 'ndt_rate') return null;
  const kind = line.unit ? NDT_QUOTE_UNIT_KIND[line.unit] : undefined;
  return kind ? NDT_LINE_UNIT_LABEL[kind] : '—';
}

/**
 * The currency for a tenant-wide quote figure (GET /quotes/summary/dashboard
 * carries no currency): the tenant's own, for an NDT tenant without casing.
 * Every quote such a tenant holds is stamped with companies.currency, so the
 * sum is in that currency. Null for every other tenant, which keeps the bare
 * '$' those screens always drew; null too while the tenant currency is unknown
 * (useTenantCurrency), so nothing guesses.
 */
export function ndtTenantQuoteCurrency(hasVertical: ((key: string) => boolean) | undefined, tenantCurrency: string | null): string | null {
  if (!hasVertical || !hasVertical('ndt_inspection') || hasVertical('casing_running')) return null;
  return tenantCurrency === 'CAD' || tenantCurrency === 'USD' ? tenantCurrency : null;
}
